import ClearIcon from '@mui/icons-material/Clear'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import Button from '@mui/material/Button'
import { message, Table, TableColumnsType } from 'antd'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'

import { chunk } from 'lodash'
import { isEmpty } from 'radash'
import CircularLoading from '../../../components/CircularProgress'
import AddressSelectSearch from '../../../components/SelectSearch/AddressSelectSearch'
import { Tag } from '../../../components/Tag'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { ImageLinks, LogoTypeProvider } from '../../../constants/image-link'
import PageUrls from '../../../constants/page-urls'
import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { useUser } from '../../../hooks/useUser'
import { IAddress } from '../../../types/address'
import {
  IOrder,
  OrderRecipient,
  OrderShippingProvider,
} from '../../../types/order'
import { PaginationResponseInterface } from '../../../types/pagination'
import { AllowShippingProviderLists } from '../../../utils/provider'
import { OptionsFlashExpress } from './OptionsFlashExpress'
import { OptionsSpxExpress } from './OptionsSpxExpress'
import { IRemotesArea } from '../../../types/remote-area'

interface ICreateTrackFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  orders: IOrder[]
  onUpdateTrackingSuccess: (successOrderIds?: string[]) => void
}

interface ICreateTrackFormModal {
  provider: OrderShippingProvider
  orders: IOrder[]
  address: IAddress
  ignoreRemoteArea?: boolean
  isDropOff?: boolean
}

interface ITracking extends IOrder {
  error?: string
}

export const CreateTrackFormModal = (props: ICreateTrackFormModalProps) => {
  const { merchant } = useUser()
  const { isOpen, onClose, orders } = props
  const [searchAddress, setSearchAddress] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [isCreateTracking, setIsCreateTracking] = useState<boolean>(false)
  const [options, setOptions] = useState<any>(undefined)

  useEffect(() => {
    setLoading(true)
    setPopupMessage('')
    formik.setFieldValue('orders', orders)
    setLoading(false)
  }, [isOpen])

  const flashRemoteArea = useAxios<IRemotesArea>({
    method: 'GET',
    url: '/remote-area/flash',
  })

  const formik = useFormik<ICreateTrackFormModal>({
    initialValues: {
      provider: OrderShippingProvider.FLASH,
      address: {} as IAddress,
      orders: [],
      ignoreRemoteArea: false,
      isDropOff: false,
    } as ICreateTrackFormModal,
    onSubmit: async (values: ICreateTrackFormModal) => {
      if (!values.address?.name) {
        message.error('กรุณาเลือกที่อยู่')
        return
      }

      if (values.orders.length === 0) {
        message.error('กรุณาเลือกออเดอร์')
        return
      }

      if (
        values.provider === OrderShippingProvider.SPXEXPRESS &&
        options?.pickUpType === 'home'
      ) {
        if (!options?.pickUpTime || options.pickUpTime === 'no') {
          message.error('กรุณาเลือกวันที่นัดรับ')
          return
        }
        if (!options?.pickupTimeRangId) {
          message.error('กรุณาเลือกช่วงเวลานัดรับ')
          return
        }
      }

      setLoading(true)
      setIsCreateTracking(true)

      let ordersNoTracking = values.orders.filter(
        (order: IOrder) => !order.trackingCode,
      )

      if (values.ignoreRemoteArea) {
        ordersNoTracking = ordersNoTracking.filter(
          (order: IOrder) =>
            !isRemoteArea(order.recipient.zipcode, order.recipient.subDistrict),
        )
        ordersNoTracking = ordersNoTracking.filter(
          (order: IOrder) =>
            !isTourArea(order.recipient.zipcode, order.recipient.subDistrict),
        )
      }
      // console.log(ordersNoTracking.length)
      // return

      const chunkNumber = 5
      const orderChunk = chunk(ordersNoTracking, chunkNumber)

      let orderResonses: any = []
      let countSuccess = 0
      for (const orders of orderChunk) {
        countSuccess += orders.length ?? 0
        try {
          const responseResult = await axiosInstance.post(
            `/trackings/${values.provider}/create`,
            {
              provider: values.provider,
              orders: orders.map((order: IOrder) => order.objectId),
              sender: {
                name: values.address.name,
                phone: values.address.phoneNumber,
                address: values.address.address,
                subDistrict: values.address.subDistrict,
                district: values.address.district,
                province: values.address.province,
                postcode: values.address.postcode,
              },
              options,
              ...(values.provider === OrderShippingProvider.JNT && {
                isDropOff: values.isDropOff ?? false,
              }),
            },
          )
          setPopupMessage(
            'กำลังสร้างหมายเลขพัสดุ ' +
            countSuccess +
            '/' +
            ordersNoTracking.length,
          )
          orderResonses = [...orderResonses, ...responseResult.data]
        } catch (e: any) {
          const messageError =
            ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
            'บันทึกข้อมูลไม่สำเร็จ **'
          message.error(messageError)
          setLoading(false)
          return
        }
      }
      setLoading(false)

      const trackingsResponse = orderResonses?.reduce((acc: any, cur: any) => {
        acc[cur?.data?.order || cur.order || 'x'] = cur
        return acc
      }, {})

      if (isEmpty(trackingsResponse)) {
        message.error('ไม่สามารถสร้าง Tracking ได้')
        setLoading(false)
        return
      }

      if (trackingsResponse?.['x']) {
        const messageText =
          orderResonses?.[0]?.errors?.message ?? 'ไม่สามารถสร้าง Tracking ได้'
        message.error(messageText)
        setLoading(false)
        return
      }

      const trackings = formik.values.orders.map<ITracking>(
        (order: ITracking) => {
          const tracking = trackingsResponse?.[order.objectId] ?? null
          if (!tracking) {
            return {
              ...order,
              trackingCode: 'ไม่สามารถสร้าง Tracking ได้',
              error: 'ไม่สามารถสร้าง Tracking ได้',
            }
          }
          let error = ''
          if (!tracking?.success) {
            error =
              tracking?.errors?.message ??
              tracking.errors.validations ??
              'ไม่สามารถสร้าง Tracking ได้'
          }
          return {
            ...order,
            trackingCode: tracking?.success ? tracking.data.tracking : '',
            error: error,
          }
        },
      )
      await formik.setFieldValue('orders', trackings)

      const successOrderIds = trackings
        .filter((t) => t.trackingCode && !t.error)
        .map((t) => t.objectId)

      if (successOrderIds.length > 0) {
        props.onUpdateTrackingSuccess(successOrderIds)
      }
    },
  })

  const addressList = useAxios<PaginationResponseInterface<IAddress>>({
    method: 'GET',
    url: '/address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate()
  }

  const isRemoteArea = (zipcode: string, subDistrict: string) => {
    if (formik.values.provider !== OrderShippingProvider.FLASH) {
      return false
    }
    const isRemote = flashRemoteArea?.data?.remotes.find(
      (remote) =>
        remote.subdistrict === subDistrict && remote.zipcode === zipcode,
    )
    return isRemote
  }

  const isTourArea = (zipcode: string, subDistrict: string) => {
    if (formik.values.provider !== OrderShippingProvider.FLASH) {
      return false
    }
    const isTour = flashRemoteArea?.data?.tours.find(
      (remote) =>
        remote.subdistrict === subDistrict && remote.zipcode === zipcode,
    )
    return isTour
  }

  const columns: TableColumnsType<ITracking & { key: number }> = [
    {
      title: 'ชื่อ',
      dataIndex: 'recipient',
      key: 'recipient',
      width: 300,
      render: (recipient: OrderRecipient) => (
        <Grid container spacing={1} flexDirection={'column'}>
          <Grid item>
            <Typography variant="h6" color="text.secondary">
              {recipient.name}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {recipient.address} {recipient.subDistrict} {recipient.district}{' '}
              {recipient.province} {recipient.zipcode}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" color="text.secondary">
              {recipient.phone}
            </Typography>
          </Grid>
        </Grid>
      ),
    },
    {
      title: 'รหัสคำสั่งซื้อ',
      dataIndex: 'orderCode',
      key: 'orderCode',
      align: 'center' as const,
      width: 80,
      render: (orderCode: number, order: IOrder) => (
        <>
          <a
            href={`${PageUrls.Orders.Detail}/${order.objectId}`}
            target="_blank"
          >
            <Tag color={'success'}>{order.code}</Tag>
          </a>
        </>
      ),
    },
    {
      title: 'รหัส Tracking',
      dataIndex: 'trackingCode',
      key: 'trackingCode',
      width: 220,
      render: (trackingCode: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {trackingCode}
        </Typography>
      ),
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'error',
      key: 'error',
      width: 220,
      render: (error: string, order: IOrder) => (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            {error ?? ''}
          </Typography>
          {isRemoteArea(
            order.recipient.zipcode,
            order.recipient.subDistrict,
          ) && <Tag color={'error'}>พื้นที่ห่างไกล</Tag>}
          {isTourArea(order.recipient.zipcode, order.recipient.subDistrict) && (
            <Tag color={'warning'}>พื้นที่ทัวร์</Tag>
          )}
        </>
      ),
    },
  ]

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="lg">
      <CircularLoading message={popupMessage} isLoading={loading} />
      <DialogTitle>สร้าง Tracking</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={4}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="type-label">เลือกขนส่ง</InputLabel>
                <Select
                  id="provider"
                  name="provider"
                  labelId="type-label"
                  value={formik.values.provider}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                >
                  <MenuItem disabled value="" selected>
                    <em>เลือกขนส่ง</em>
                  </MenuItem>
                  {AllowShippingProviderLists(merchant).map(
                    (item, index: number) => (
                      <MenuItem value={item.value} key={index}>
                        {item.value !== 'all' && (
                          <img
                            style={{ marginRight: 5 }}
                            src={
                              ImageLinks[
                              `LOGO_${item.value.toUpperCase()}` as LogoTypeProvider
                              ]
                            }
                            alt="smallLogoSocial"
                            height={20}
                          />
                        )}
                        {item.label}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={8}>
              {/* <SelectSearch<any>
                label={'เลือกที่อยู่'}
                onSelect={(e) => formik.setFieldValue('address', e?.value)}
                search={searchAddress}
                onSearch={handleSearchAddress}
                options={
                  addressList.data?.records.map((address: IAddress) => ({
                    label: `${address.name}, โทร. ${address.phoneNumber} ,${address.address} ${address.subDistrict} ${address.district} ${address.province} ${address.postcode}`,
                    value: address,
                  })) || ([] as IOptionSelect<any>[])
                }
              /> */}
              <AddressSelectSearch
                label="เลือกที่อยู่ผู้ส่ง"
                onSelect={(e) => formik.setFieldValue('address', e?.value)}
              />
            </Grid>

            {formik.values.provider === OrderShippingProvider.JNT && (
              <Grid item xs={12} lg={12}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="isDropOff-label">Dropoff</InputLabel>
                  <Select
                    id="isDropOff"
                    name="isDropOff"
                    labelId="isDropOff-label"
                    value={Number(formik.values.isDropOff ?? false)}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'isDropOff',
                        Boolean(Number(e.target.value)),
                      )
                    }
                    onBlur={formik.handleBlur}
                    fullWidth
                  >
                    <MenuItem value={0}>ไม่ใช่</MenuItem>
                    <MenuItem value={1}>ใช่ (ลงจุด Dropoff)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {formik.values.provider === OrderShippingProvider.FLASH && (
              <OptionsFlashExpress setOptions={setOptions} options={options} />
            )}
            {formik.values.provider === OrderShippingProvider.SPXEXPRESS && (
              <OptionsSpxExpress setOptions={setOptions} options={options} />
            )}
            <Grid item xs={12} lg={12}>
              <Table
                size={'middle'}
                columns={columns}
                pagination={false}
                dataSource={formik.values.orders?.map(
                  (tracking: any, index: number) => {
                    return {
                      ...tracking,
                      key: index,
                    }
                  },
                )}
                bordered
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <Grid container spacing={2} justifyContent="flex-end">
                {formik.values.provider === OrderShippingProvider.FLASH && (
                  <>
                    <Grid item>
                      <Button
                        disabled={loading}
                        color="secondary"
                        type="button"
                        size="large"
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={() => {
                          formik.setFieldValue('ignoreRemoteArea', true)
                          formik.handleSubmit()
                        }}
                      >
                        สร้างทั้งหมด ยกเว้นพื้นที่พิเศษ
                      </Button>
                    </Grid>
                  </>
                )}
                <Grid item>
                  <Button
                    disabled={loading}
                    color="primary"
                    type="button"
                    size="large"
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => {
                      formik.setFieldValue('ignoreRemoteArea', false)
                      formik.handleSubmit()
                    }}
                  >
                    สร้างทั้งหมด
                  </Button>
                </Grid>

                {/*<Grid item>*/}
                {/*  <Button*/}
                {/*    color="secondary"*/}
                {/*    type="submit"*/}
                {/*    size="large"*/}
                {/*    variant="contained"*/}
                {/*    startIcon={<SendIcon />}*/}
                {/*  >*/}
                {/*    ยืนยันยกเว้นพื้นที่พิเศษ*/}
                {/*  </Button>*/}
                {/*</Grid>*/}
                <Grid item>
                  <Button
                    color="inherit"
                    size="large"
                    variant="contained"
                    onClick={() => onClose(false)}
                    startIcon={<ClearIcon />}
                  >
                    ปิดหน้าต่าง
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
